import Icon from '../components/Icon';
import { displayPatientCode } from '../utils/patientCode';
// src/pages/DoctorPatientDetailPage.jsx
//
// Sprint 11 changes:
//   1. DiseaseRiskView wrapped in ErrorBoundary — prevents a JSON parse crash
//      in claudeRawResponse from taking down the entire patient detail page.
//      Constitution §III: disease risk is a mandatory triage output dimension.
//
//   2. Critical escalation data now fetched from
//      GET /api/doctor/critical-escalations?patientId= and passed into
//      triageReports so DoctorTriageView's EscalationSlaBadge has the data it needs.
//      Constitution §III: "2h SLA for CRITICAL acknowledgement."
//
//   NOTE: The TriageReportDto from the backend now includes escalationStatus +
//   escalationNotifiedAt directly (DoctorPortalService Sprint 11). The separate
//   escalation fetch here is a belt-and-braces approach so the badge works even
//   if the DTO field hasn't been deployed yet — it merges both sources.

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import VitalsIntakeForm from '../components/VitalsIntakeForm';
import DoctorTriageView, { DoctorNotesTab } from '../components/doctor/DoctorTriageView';
import DoctorDocumentsSection from '../components/doctor/DoctorDocumentsSection';
import SlideOverDrawer from '../components/doctor/SlideOverDrawer';
import PatientSnapshot from '../components/doctor/PatientSnapshot';
import SafetyStrip from '../components/doctor/SafetyStrip';
import VisitsTab from '../components/doctor/VisitsTab';
import VitalsTab from '../components/doctor/VitalsTab';
import OpenItemsList from '../components/doctor/OpenItemsList';
import useDoctorPatientClinicData from '../hooks/useDoctorPatientClinicData';
import { buildTimeline, lastSignedVisit } from '../utils/visitTimeline';
import {
  WhatThisMeans, WhatToDoNext, parseReportMarkers, NORMAL_STATUSES,
} from '../components/PatientResultSections';
import { buildMarkerHistoryMap } from '../utils/markerHistory';
import EmptyState from '../components/EmptyState';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  doctorPatientAPI,
  triageReportAPI,
  doctorActionItemAPI,
  doctorNoteAPI,
  doctorEscalationAPI,
  doctorLifestyleItemAPI,
  doctorAppointmentAPI,
} from '../services/doctorApi';


// ── Helpers ────────────────────────────────────────────────────────────────────

function initials(first, last) {
  return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '?';
}

function InfoRow({ label, value }) {
  return (
    <div className="prec-kv">
      <span className="prec-kv__k">{label}</span>
      <span className="prec-kv__v">
        {value ?? <span className="prec-kv__none">—</span>}
      </span>
    </div>
  );
}

// Rows that aren't clinical data: QA/seed rows titled "[TEST] …" and blank
// rows with no text at all. Kept out of the doctor's counts, checklist and notes.
const isHiddenRecord = (text) => !text || !text.trim() || /^\s*\[TEST\]/i.test(text);

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Disease Risk helpers ──────────────────────────────────────────────────────

// Colour dot instead of an emoji: emoji ignore the theme and currentColor. The
// text label always sits beside it, so colour is never the only signal.
const DOT_COLOR = { normal: 'var(--green)', elevated: 'var(--amber)', critical: 'var(--red)', low: 'var(--blue)' };
function StatusDot({ status }) {
  return <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: DOT_COLOR[status] || 'var(--text-muted)' }} />;
}
const RISK_COLOR  = { low: 'var(--green)', moderate: 'var(--amber-text)', high: 'var(--red)' };
const RISK_BG     = { low: 'var(--green-bg)', moderate: 'var(--amber-bg)', high: 'var(--red-bg)' };
const RISK_BORDER = { low: 'var(--green)', moderate: 'var(--amber)', high: 'var(--red)' };

function parseClaudeRaw(raw) {
  if (!raw) return null;
  try {
    const clean = raw.replace(/```json|```/g, '').trim();
    return JSON.parse(clean);
  } catch {
    return null;
  }
}

function DiseaseRiskBadge({ level }) {
  if (!level) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 12px', borderRadius: 100,
      fontSize: 12, fontWeight: 700,
      letterSpacing: 0.5, textTransform: 'uppercase',
      background: RISK_BG[level] || 'var(--bg-canvas)',
      color: RISK_COLOR[level] || 'var(--text-primary)',
      border: `1px solid ${RISK_BORDER[level] || 'var(--border)'}`,
    }}>
      {level.toUpperCase()} RISK
    </span>
  );
}

function DiseaseScoreBar({ score, level }) {
  const barColor = level === 'high'
    ? 'linear-gradient(90deg,var(--red),var(--red))'
    : level === 'moderate'
    ? 'linear-gradient(90deg,var(--amber),var(--amber))'
    : 'linear-gradient(90deg,var(--green),var(--green))';
  return (
    <div style={{ margin: '8px 0 4px' }}>
      <div style={{ height: 7, background: 'var(--bg-canvas)', borderRadius: 100, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 100,
          width: `${score ?? 0}%`,
          background: barColor,
          transition: 'width 0.6s ease',
        }} />
      </div>
    </div>
  );
}

function DiseaseMarkersTable({ markers }) {
  if (!markers?.length) return null;
  return (
    <div style={{ overflowX: 'auto', marginBottom: 16 }}>
      <table className="lt-markers-table">
        <thead>
          <tr>
            <th>Marker</th><th>Value</th><th>Reference</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {markers.map((m, i) => (
            <tr key={i}>
              <td><strong>{m.name}</strong></td>
              <td>
                <span className="lt-marker-val">
                  {typeof m.value === 'number' ? m.value.toFixed(2) : (m.value ?? '—')}
                </span>
              </td>
              <td><span className="lt-marker-ref">{m.ref ?? m.reference ?? '—'}</span></td>
              <td>
                <span className={`lt-status ${m.status === 'low' ? 'low' : m.status}`}>
                  <StatusDot status={m.status} /> {(m.status ?? '—').toUpperCase()}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * DiseaseRiskView — renders claudeRawResponse JSON as a disease risk panel.
 *
 * Sprint 11: wrapped in ErrorBoundary at call site.
 * Constitution §III: disease risk is a mandatory triage output dimension.
 *
 * If claudeRawResponse is null or unparseable, renders a graceful fallback
 * instructing the user to re-run triage — does NOT throw.
 */
function DiseaseRiskView({ report }) {
  const parsed = parseClaudeRaw(report.claudeRawResponse);

  if (!parsed) {
    return (
      <p className="field-text">
        Disease risk detail is not available for this report. Re-run triage to generate it.
      </p>
    );
  }

  const claudeLevel = parsed.risk_level?.toLowerCase();

  return (
    <div>
      <div className="field-label" style={{ paddingBottom: 10, borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
        Disease Risk Analysis · {fmtDate(report.createdAt)}
      </div>

      {/* Agreement banner removed (Sep 2026, RF disabled) — there is only one model now. */}

      {/* Model cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Claude AI card */}
        <div style={{
          flex: '1 1 300px', border: '1px solid var(--border)', borderRadius: 12,
          padding: '16px 18px', background: 'var(--bg-surface)',
          borderTop: '3px solid var(--brand-dark)',
        }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 2 }}>
              Large Language Model · Anthropic
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, color: 'var(--brand-dark)' }}>
              Claude
            </div>
            <div style={{ marginTop: 6 }}>
              <DiseaseRiskBadge level={claudeLevel} />
            </div>
          </div>

          <DiseaseMarkersTable markers={parsed.markers?.map(m => ({ ...m, ref: m.reference }))} />

          {parsed.key_findings && (
            <div className="lt-findings" style={{ marginBottom: 10 }}>
              <div className="lt-findings-title">Key Findings</div>
              <div className="lt-findings-text">{parsed.key_findings}</div>
            </div>
          )}

          {parsed.recommendation && (
            <div className="lt-recommendation">
              <div className="lt-recommendation-title">Recommendation</div>
              <div className="lt-recommendation-text">{parsed.recommendation}</div>
            </div>
          )}

          {parsed.missing_markers?.length > 0 && (
            <div className="lt-missing" style={{ marginTop: 10 }}>
              <Icon name="alert" size={15} />
              <span><strong>Missing:</strong> {parsed.missing_markers.join(', ')}</span>
            </div>
          )}
        </div>

        {/* Random Forest summary card removed (Sep 2026, RF disabled). */}
        <div style={{
          flex: '1 1 300px', border: '1px solid var(--border)', borderRadius: 12,
          padding: '16px 18px', background: 'var(--bg-surface)',
          borderTop: '3px solid var(--brand-accent)',
        }}>
          <div style={{
            padding: '10px 14px', background: 'var(--amber-bg)',
            borderLeft: '3px solid var(--brand-accent)',
            borderRadius: '0 8px 8px 0',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--brand-accent)', marginBottom: 3 }}>
              Consensus
            </div>
            <div style={{ fontSize: 13, color: 'var(--brand-dark)', lineHeight: 1.55 }}>
              {report.consensusRiskScore != null
                ? `Consensus risk score: ${report.consensusRiskScore} / 100 · ${report.consensusRiskLevel}`
                : 'Consensus score not available.'}
            </div>
          </div>
        </div>
      </div>

      <div className="lt-disclaimer">
        <strong>⚠ Disclaimer:</strong> This model is for informational purposes only.
        Not a substitute for clinical diagnosis. Always refer findings to a qualified physician.
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const PRIORITY_COLORS = {
  P1: { bg: 'var(--red)', label: 'Immediate',   sub: 'Act within 48 hours' },
  P2: { bg: 'var(--amber-text)', label: 'Short-term',  sub: 'Within 2 weeks' },
  P3: { bg: 'var(--blue)', label: 'Ongoing',     sub: 'Schedule and monitor' },
};

const RISK_BADGE = { HIGH: 'badge-critical', MODERATE: 'badge-moderate', LOW: 'badge-low' };

function ExpandableText({ text, max = 200 }) {
  const [exp, setExp] = useState(false);
  if (!text || text.length <= max) return <span>{text}</span>;
  return (
    <span>
      {exp ? text : text.slice(0, max) + '…'}
      <button
        onClick={() => setExp(v => !v)}
        style={{ background: 'none', border: 'none', color: 'var(--brand-accent)', cursor: 'pointer', fontSize: 12, marginLeft: 4 }}
      >
        {exp ? 'Show less' : 'Show more'}
      </button>
    </span>
  );
}

/**
 * PreviousReports — collapsible prior triage history.
 * Sprint 11: DiseaseRiskView inside here is now wrapped in ErrorBoundary.
 */
function PreviousReports({ reports, fields }) {
  const [expanded, setExpanded] = useState(null);
  if (!reports?.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <div className="field-label" style={{ marginTop: 8 }}>
        Previous reports ({reports.length})
      </div>
      {reports.map((r, idx) => (
        <div key={r.id || idx} className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 8 }}>
          <button
            className="card-row" style={{ border: 'none', borderRadius: 0 }}
            onClick={() => setExpanded(expanded === idx ? null : idx)}
          >
            <span>Blood Test Triage · {fmtDate(r.createdAt)}</span>
            <span className={`badge ${RISK_BADGE[r.consensusRiskLevel] || 'badge-unknown'}`}>
              {r.consensusRiskLevel} · {r.consensusRiskScore}
            </span>
            <span>{expanded === idx ? '▲' : '▼'}</span>
          </button>
          {expanded === idx && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fields.includes('findings') && r.claudeKeyFindings && (
                <p className="field-text"><ExpandableText text={r.claudeKeyFindings} /></p>
              )}
              {fields.includes('findings') && r.claudeRecommendation && (
                <div className="field-group">
                  <div className="field-label">Recommendation</div>
                  <p className="field-text"><ExpandableText text={r.claudeRecommendation} /></p>
                </div>
              )}
              {fields.includes('action') && r.actionPlan?.map((item, i) => (
                <div key={i} className="field-group">
                  <div className="field-label">{item.category} · {item.window}</div>
                  <div className="field-text" style={{ fontWeight: 500 }}>{item.title}</div>
                  <p className="field-text">{item.body}</p>
                </div>
              ))}
              {fields.includes('lifestyle') && r.lifestyleRecommendation && (
                <p className="field-text"><ExpandableText text={r.lifestyleRecommendation} /></p>
              )}
              {/* Sprint 11: DiseaseRiskView wrapped in ErrorBoundary */}
              {fields.includes('disease') && r.claudeRawResponse && (
                <div style={{ marginTop: 12 }}>
                  <ErrorBoundary label="Disease risk panel">
                    <DiseaseRiskView report={r} />
                  </ErrorBoundary>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DoctorPatientDetailPage() {
  const { patientId } = useParams();
  const navigate      = useNavigate();
  const { user }      = useAuth();
  const { toast }     = useToast();
  const headingRef    = useRef(null);

  const [patient,       setPatient]       = useState(null);
  const [triageReports, setTriageReports] = useState([]);
  const [doctorItems,   setDoctorItems]   = useState([]);
  const [notes,         setNotes]         = useState([]);
  const [lifestyleItems, setLifestyleItems] = useState([]);
  const [appointments,  setAppointments]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  // 20 Jul 2026 UX pass: Documents/Notes moved out of DoctorTriageView's
  // tab bar into icon buttons on the hero card, next to Chat — they're
  // reference material about the patient, not interpretations of a
  // specific triage result, so grouping them with Chat (also patient-level,
  // not result-level) made more sense than living among the 7 clinical tabs.
  const [drawer, setDrawer] = useState(null); // null | 'documents' | 'notes' | 'items'
  // "View" from My Patients lands on the patient's details; the triage
  // report is one click away on its own tab.
  // `?tab=Visits&visit=<id>` (from a signed consultation) opens straight onto that visit.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => (
    ['Overview', 'Visits', 'Vitals', 'Health Records'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Overview'));
  // A visit to expand and scroll to once the Visits tab shows (set by the Last visit
  // tile's "Open →" and the vitals table's "Visit →").
  const [recordingVitals, setRecordingVitals] = useState(false);
  const [focusVisitId, setFocusVisitId] = useState(() => searchParams.get('visit') || null);
  const handleFocusHandled = useCallback(() => setFocusVisitId(null), []);
  const openVisit = (id) => { setSelectedReportId(null); setFocusVisitId(id); setActiveTab('Visits'); };
  const goToTab = (tab) => { setSelectedReportId(null); setActiveTab(tab); };
  // Which health record's triage report is open on the Health Records tab;
  // null = the list of records.
  const [selectedReportId, setSelectedReportId] = useState(null);
  const openReport = (id) => { setSelectedReportId(id); setActiveTab('Health Records'); };

  // Escalation data by triage-result id, kept in a ref so it can be merged into
  // the reports whichever of the two requests finishes first.
  const escalationMapRef = useRef({});
  const [extrasLoading, setExtrasLoading] = useState(false);
  const [extrasFailed,  setExtrasFailed]  = useState([]);

  // The DTO already carries escalationStatus + escalationNotifiedAt (Sprint 11
  // backend); the separate escalation fetch is a belt-and-braces fallback for
  // the deployment window. DTO field wins.
  const mergeEscalations = useCallback((reports) => reports.map(r => {
    if (r.escalationStatus) return r;
    const esc = escalationMapRef.current[r.id];
    return esc
      ? { ...r, escalationStatus: esc.status, escalationNotifiedAt: esc.notifiedAt }
      : r;
  }), []);

  // Everything except the patient and their triage reports. None of it is
  // needed to render the hero, tabs and latest report, so it loads in the
  // background and fills in as each call returns — a slow or failed call
  // here never holds the page on its skeleton.
  const loadExtras = useCallback((isCancelled = () => false) => {
    const rows = (res) => res.data?.content || res.data || [];
    const calls = [
      ['escalations', doctorEscalationAPI.listByPatient(patientId), (res) => {
        escalationMapRef.current = Object.fromEntries(rows(res).map(e => [e.triageResultId, e]));
        setTriageReports(prev => mergeEscalations(prev));
      }],
      ['action items', doctorActionItemAPI.listByPatient(patientId), (res) => setDoctorItems(rows(res).filter(i => !isHiddenRecord(i.description)))],
      ['notes',        doctorNoteAPI.listByPatient(patientId),       (res) => setNotes(rows(res).filter(n => !isHiddenRecord(n.content)))],
      ['lifestyle items', doctorLifestyleItemAPI.listByPatient(patientId), (res) => setLifestyleItems(rows(res))],
      // The doctor's appointment list spans all their patients — keep this one's.
      ['appointments', doctorAppointmentAPI.list(user?.email, 'all'),
        (res) => setAppointments(rows(res).filter(a => a.patient?.id === patientId))],
    ];
    setExtrasLoading(true);
    setExtrasFailed([]);
    return Promise.all(calls.map(([label, promise, apply]) =>
      promise.then(res => { if (!isCancelled()) apply(res); })
             .catch(() => { if (!isCancelled()) setExtrasFailed(prev => [...prev, label]); })
    )).finally(() => { if (!isCancelled()) setExtrasLoading(false); });
  }, [patientId, user?.email, mergeEscalations]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    async function load() {
      setLoading(true);
      setError(null);
      escalationMapRef.current = {};
      // Only the patient and their triage reports gate the page.
      const [patRes, triRes] = await Promise.allSettled([
        doctorPatientAPI.getDetail(patientId, user?.email),
        triageReportAPI.listByPatient(patientId),
      ]);
      if (cancelled) return;

      if (patRes.status === 'rejected' || triRes.status === 'rejected') {
        setError('Could not load patient data. Please try again.');
        setLoading(false);
        return;
      }

      const reports = triRes.value.data?.content || triRes.value.data || [];
      setPatient(patRes.value.data);
      setTriageReports(mergeEscalations(reports));
      setLoading(false);
    }
    load();
    loadExtras(isCancelled);
    return () => { cancelled = true; };
  }, [patientId, user?.email, loadExtras, mergeEscalations]);

  // Vitals, this doctor's visits and prescriptions: loaded after the patient, each on its own.
  const clinicRaw = useDoctorPatientClinicData(patientId, user?.email, !!patient);
  const clinic = useMemo(() => {
    const section = (name) => ({ ...clinicRaw[name], reload: () => clinicRaw.reload(name) });
    return { vitals: section('vitals'), visits: section('visits'), prescriptions: section('prescriptions') };
  }, [clinicRaw]);
  const today = useMemo(() => new Date(), [patientId]); // eslint-disable-line react-hooks/exhaustive-deps
  const timeline = useMemo(() => buildTimeline({
    visits: clinic.visits.data, prescriptions: clinic.prescriptions.data,
    vitals: clinic.vitals.data, appointments,
  }, today), [clinic.visits.data, clinic.prescriptions.data, clinic.vitals.data, appointments, today]);
  const signedVisitCount = timeline.rows.filter(r => r.kind === 'visit').length;
  const latestSigned = useMemo(
    () => lastSignedVisit(clinic.visits.data, appointments), [clinic.visits.data, appointments]);

  useEffect(() => {
    if (patient) {
      document.title = `${patient.firstName} ${patient.lastName} — Previu`;
      return () => { document.title = 'Previu — Patient Management'; };
    }
  }, [patient]);

  useEffect(() => {
    if (patient && headingRef.current) {
      headingRef.current.focus();
    }
  }, [patient]);

  const handleToggleItem = useCallback(async (id, completed) => {
    setDoctorItems(prev => prev.map(i => i.id === id ? { ...i, completed } : i));
    try {
      await doctorActionItemAPI.update(id, { completed });
    } catch {
      toast.error("Couldn't save — please try again");
      setTimeout(() => {
        setDoctorItems(prev => prev.map(i => i.id === id ? { ...i, completed: !completed } : i));
      }, 3000);
    }
  }, [toast]);

  const handleAddItem = useCallback(async ({ description, priority, dueDate }) => {
    try {
      const res = await doctorActionItemAPI.create({
        patientId,
        doctorEmail: user?.email,
        description,
        priority,
        ...(dueDate ? { dueDate } : {}),
      });
      setDoctorItems(prev => [res.data, ...prev]);
      toast.success('Prescription item added');
    } catch {
      toast.error("Couldn't add item — please try again");
    }
  }, [patientId, user?.email, toast]);

  const handleAddNote = useCallback(async (content) => {
    try {
      const res = await doctorNoteAPI.create({
        patientId,
        doctorEmail: user?.email,
        content,
      });
      setNotes(prev => [res.data, ...prev]);
      toast.success('Note saved');
    } catch {
      toast.error("Couldn't save note — please try again");
    }
  }, [patientId, user?.email, toast]);

  // Accepting a lifestyle point creates a doctor_action_items row on the
  // backend (source=AI_ACCEPTED) and is the only way one ever reaches the
  // patient/navigator — see LifestyleRecommendationService.java. The updated
  // row (now ACCEPTED, with its actionItemId) replaces the local item so the
  // review tab reflects the new status without a re-fetch.
  const handleAcceptLifestyleItem = useCallback(async (id) => {
    try {
      const res = await doctorLifestyleItemAPI.accept(id, user?.email);
      setLifestyleItems(prev => prev.map(i => i.id === id ? res.data : i));
      toast.success('Recommendation accepted — added to care plan');
    } catch {
      toast.error("Couldn't accept — please try again");
    }
  }, [user?.email, toast]);

  const handleRejectLifestyleItem = useCallback(async (id) => {
    try {
      const res = await doctorLifestyleItemAPI.reject(id, user?.email);
      setLifestyleItems(prev => prev.map(i => i.id === id ? res.data : i));
    } catch {
      toast.error("Couldn't reject — please try again");
    }
  }, [user?.email, toast]);

  // Saves the doctor's 1-2 "needs attention" marker picks for one report
  // (HighlightedMarkersPicker in DoctorTriageView) and reflects the result
  // in local state so the picker shows the saved picks without a re-fetch.
  // Re-throws on failure so the picker's own catch can show its inline error
  // and NOT flip out of edit mode — see HighlightedMarkersPicker.handleSave.
  const handleSetHighlightedMarkers = useCallback(async (triageResultId, markers) => {
    const res = await triageReportAPI.setHighlightedMarkers(triageResultId, {
      doctorEmail: user?.email,
      markers,
    });
    setTriageReports(prev => prev.map(r => (r.id === triageResultId
      ? { ...r, highlightedMarkers: res.data }
      : r)));
  }, [user?.email]);

  // Patches the one report's consensus fields in local state after a
  // confound-driven re-evaluation (PatientConfoundsTab) — the API call
  // itself already happened there; this just reflects the new verdict
  // without a full re-fetch of everything else on the page.
  const handleTriageReevaluated = useCallback((triageResultId, reevaluation) => {
    setTriageReports(prev => prev.map(r => (r.id === triageResultId
      ? { ...r, consensusRiskLevel: reevaluation.newRiskLevel, consensusRiskScore: reevaluation.newRiskScore }
      : r)));
  }, []);

  // Flagged markers of the latest report — drives Clinical Summary (count only;
  // the names are listed in "What this means" just below), and is the same
  // filter WhatThisMeans applies, so the two can't disagree.
  const flaggedNames = parseReportMarkers(triageReports[0])
    .filter(m => !NORMAL_STATUSES.has((m.status || m.severity || 'normal').toLowerCase()))
    .map(m => m.name || m.marker)
    .filter(Boolean);
  const flaggedSummary = flaggedNames.length
    ? `${flaggedNames.length} flagged`
    : (triageReports[0] ? 'None flagged' : null);

  if (loading) return (
    <AppShell breadcrumbs={[{ label: 'My Patients', to: '/doctor/patients' }, { label: 'Patient' }]}>
      <div className="skeleton-list">
        <div className="skeleton skeleton--hero" />
        <div className="skeleton skeleton--tabs" />
        <div className="skeleton skeleton--body" />
      </div>
    </AppShell>
  );

  if (error || !patient) return (
    <AppShell breadcrumbs={[{ label: 'My Patients', to: '/doctor/patients' }, { label: 'Patient' }]}>
      <div className="empty-state">
        <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="search" size={20} /></div>
        <div className="empty-title">{error || 'Patient not found.'}</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }}
          onClick={() => navigate('/doctor/patients')}>Back to patients</button>
      </div>
    </AppShell>
  );

  return (
    <AppShell breadcrumbs={[
      { label: 'My Patients', to: '/doctor/patients' },
      { label: `${patient.firstName} ${patient.lastName}` }
    ]}>

      {/* Back link — canonical .back-link */}
      <button className="back-link" onClick={() => navigate('/doctor/patients')}>
        ← My Patients
      </button>

      {/* The patient and their reports render as soon as they arrive; the
          rest (notes, action items, appointments, …) fills in behind. */}
      {extrasLoading && (
        <div className="alert alert-info" role="status" style={{ marginBottom: 16 }}>
          Loading notes, action items and appointments…
        </div>
      )}
      {!extrasLoading && extrasFailed.length > 0 && (
        <div className="alert alert-warning" role="alert" style={{ marginBottom: 16 }}>
          Could not load {extrasFailed.join(', ')}. What is shown may be incomplete.{' '}
          <button className="btn btn-sm" onClick={() => loadExtras()}>Retry</button>
        </div>
      )}

      {/* Hero — canonical .patient-hero, same component the navigator's
          PatientDetailPage uses */}
      <div className="patient-hero fade-in-up">
        <div className="patient-avatar-lg" aria-hidden="true">
          {initials(patient.firstName, patient.lastName)}
        </div>
        <div style={{ position: 'relative', zIndex: 1, minWidth: 0 }}>
          <h2
            className="patient-name-lg"
            ref={headingRef}
            tabIndex={-1}
            style={{ outline: 'none' }}
          >
            {patient.firstName} {patient.lastName}
          </h2>
          <div className="patient-meta-lg">
            {[
              patient.age ? `${patient.age} yrs` : null,
              patient.gender,
              patient.phone,
              displayPatientCode(patient.patientCode),
            ].filter(Boolean).join(' · ')}
          </div>
          <div className="patient-meta-lg" style={{ marginTop: 4 }}>
            Navigator: {patient.navigatorName || 'Unassigned'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {patient.triagePriority === 'HIGH' && (
              <span className="badge badge-critical">Critical</span>)}
            {patient.visitCount != null && (
              <span className="badge badge-confirmed">
                {patient.visitCount} visit{patient.visitCount !== 1 ? 's' : ''}
              </span>)}
          </div>
        </div>
        <div className="patient-hero-actions">
          <button className="btn btn-ghost"
            onClick={() => navigate(`/doctor/patients/${patientId}/chat`)}>Chat</button>
          <button
            className="btn btn-ghost"
            onClick={() => setDrawer('documents')}
            aria-label="Open documents"
          >
            Documents
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => setDrawer('notes')}
            aria-label="Open clinical notes"
          >
            Notes
          </button>
        </div>
      </div>

      {/* Tier 1 — safety strip: allergies, active medicines, latest vitals. Always visible. */}
      <SafetyStrip
        patient={patient}
        vitals={clinic.vitals}
        prescriptions={clinic.prescriptions}
        today={today}
        onOpenVisits={() => goToTab('Visits')}
        onOpenVitals={() => goToTab('Vitals')}
      />

      {/* Tier 2 — orientation: risk, open items, last visit, next visit. */}
      <PatientSnapshot
        reports={triageReports}
        doctorItems={doctorItems}
        lifestyleItems={lifestyleItems}
        appointments={appointments}
        lastSignedVisit={latestSigned}
        fallbackLastVisitDate={patient.lastVisitDate}
        onOpenVisit={openVisit}
        itemsStatus={extrasFailed.includes('action items') ? 'failed' : extrasLoading ? 'loading' : 'ready'}
        onOpenItems={() => setDrawer('items')}
        onOpenReports={() => { setSelectedReportId(triageReports[0]?.id ?? null); setActiveTab('Health Records'); }}
      />

      {/* Tier 3 — one tab per question. */}
      <div className="tabs-bar fade-in-up delay-2">
        {['Overview', 'Visits', 'Vitals', 'Health Records'].map(t => (
          <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`}
            aria-current={activeTab === t ? 'page' : undefined}
            onClick={() => goToTab(t)}>
            {t}
            {t === 'Visits' && clinic.visits.status === 'ready' && signedVisitCount > 0 && (
              <span className="tab-count tab-count--neutral">{signedVisitCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview: clinical content first; contact / care team / summary in a side card. */}
      {activeTab === 'Overview' && (
        <div className="prec-overview">
          <div className="prec-overview__main fade-in-up">
            {triageReports[0] ? (
              <ErrorBoundary label="Patient-facing summary">
                <WhatThisMeans
                  result={triageReports[0]}
                  markers={parseReportMarkers(triageReports[0])}
                  markerHistory={buildMarkerHistoryMap(triageReports)}
                  audience="doctor"
                />
                <WhatToDoNext result={triageReports[0]} title="AI recommendations" audience="doctor" />
              </ErrorBoundary>
            ) : (
              <div className="card">
                <div className="empty-state prec-empty">
                  <div className="empty-title">No health records yet</div>
                  <div className="empty-desc">The summary and AI recommendations appear once a report has been triaged.</div>
                </div>
              </div>
            )}
          </div>
          <aside className="card prec-side fade-in-up delay-1" aria-label="Patient details">
            <h3 className="prec-side__h">Contact</h3>
            <InfoRow label="Mobile" value={patient.phone} />
            <InfoRow label="Email" value={patient.email} />
            <h3 className="prec-side__h">Care team</h3>
            <InfoRow label="Navigator" value={patient.navigatorName || 'Unassigned'} />
            <h3 className="prec-side__h">Clinical summary</h3>
            {/* Same source as "What this means" (the latest report's flagged markers).
                patient.primaryCondition is only the first 60 characters of the AI
                key-findings text, which names markers that are fine as often as ones that aren't. */}
            <InfoRow label="Flagged markers" value={flaggedSummary} />
            <InfoRow label="Triage priority" value={patient.triagePriority} />
            <InfoRow label="Triage score" value={patient.triageScore} />
            <InfoRow label="Visits" value={patient.visitCount} />
          </aside>
        </div>
      )}

      {activeTab === 'Visits' && (
        <div className="fade-in-up">
          <ErrorBoundary label="Visits">
            <VisitsTab
              rows={timeline.rows}
              visits={clinic.visits}
              prescriptions={clinic.prescriptions}
              vitals={clinic.vitals}
              focusVisitId={focusVisitId}
              notes={notes}
              onFocusHandled={handleFocusHandled}
              onReload={clinicRaw.reload}
            />
          </ErrorBoundary>
        </div>
      )}

      {activeTab === 'Vitals' && (
        <div className="fade-in-up">
          <ErrorBoundary label="Vitals">
            <VitalsTab vitals={clinic.vitals} timelineRows={timeline.rows} onOpenVisit={openVisit} onReload={clinicRaw.reload} onRecord={() => setRecordingVitals(true)} />
          </ErrorBoundary>
        </div>
      )}

      {activeTab === 'Health Records' && !selectedReportId && (
        <div className="card fade-in-up">
          <div className="card-title">Health Records</div>
          {triageReports.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No health records yet.</div>
          ) : triageReports.map(r => {
            const level = (r.consensusRiskLevel || '').toUpperCase();
            const badge = level === 'HIGH' || level === 'CRITICAL' ? 'badge-critical'
              : level === 'MODERATE' ? 'badge-moderate' : level === 'LOW' ? 'badge-low' : 'badge-unknown';
            return (
              <div key={r.id} className="card-row" role="button" tabIndex={0}
                onClick={() => openReport(r.id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openReport(r.id); } }}>
                <div className="card-row-body">
                  <div className="card-row-title">
                    {r.drawDate
                      ? new Date(r.drawDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                      : fmtDate(r.createdAt)}
                  </div>
                  <div className="card-row-meta">
                    Report generated {fmtDate(r.createdAt)}{r.triageType ? ` · ${r.triageType}` : ''}
                  </div>
                </div>
                <span className={`badge ${badge}`}>
                  {level || 'UNKNOWN'}{r.consensusRiskScore != null ? ` · ${r.consensusRiskScore}` : ''}
                </span>
                <span aria-hidden="true">→</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Selected health record's triage report — DoctorTriageView handles all sub-tabs */}
      {activeTab === 'Health Records' && selectedReportId && (
      <div>
        <button className="back-link" onClick={() => setSelectedReportId(null)}>
          ← Health Records
        </button>
        {/*
          Sprint 11:
          - DoctorTriageView receives enrichedReports[0] which now includes
            escalationStatus + escalationNotifiedAt fields wired from backend.
          - EscalationSlaBadge in DoctorTriageView's ReportHeader uses these
            to render the 2h SLA countdown timer or overdue indicator.
          - The ErrorBoundary wrapping DoctorTriageView catches any render crash
            (e.g. malformed claudeRawResponse) without taking down the page.
        */}
        <ErrorBoundary label="Triage report">
          <DoctorTriageView
            report={triageReports.find(r => r.id === selectedReportId) || null}
            patient={patient}
            allReports={triageReports}
            doctorItems={doctorItems}
            notes={notes}
            lifestyleItems={lifestyleItems}
            onToggleItem={handleToggleItem}
            onAddItem={handleAddItem}
            onAddNote={handleAddNote}
            onAcceptLifestyleItem={handleAcceptLifestyleItem}
            onRejectLifestyleItem={handleRejectLifestyleItem}
            currentUserId={user?.id}
            onReevaluated={handleTriageReevaluated}
            onSetHighlightedMarkers={handleSetHighlightedMarkers}
          />
        </ErrorBoundary>
      </div>
      )}

      {recordingVitals && (
        <VitalsIntakeForm patientId={patientId} patientName={patient ? `${patient.firstName} ${patient.lastName}` : ''}
          onClose={() => setRecordingVitals(false)} onSaved={() => clinicRaw.reload('vitals')} />
      )}

      {/* ── Documents / Notes — slide-over drawers, opened from the hero
           card icons above (20 Jul 2026 UX pass). Same components DoctorTriageView
           used to render as tabs — just reached from the patient-level hero
           card now instead of the result-level tab bar. ── */}
      <SlideOverDrawer
        open={drawer === 'documents'}
        title="Documents"
        icon="document"
        onClose={() => setDrawer(null)}
      >
        <ErrorBoundary label="Documents">
          <DoctorDocumentsSection patientId={patientId} />
        </ErrorBoundary>
      </SlideOverDrawer>

      <SlideOverDrawer
        open={drawer === 'items'}
        title="Open items"
        icon="note"
        onClose={() => setDrawer(null)}
      >
        <OpenItemsList items={doctorItems} onToggle={handleToggleItem} />
      </SlideOverDrawer>

      <SlideOverDrawer
        open={drawer === 'notes'}
        title="Clinical Notes"
        icon="note"
        onClose={() => setDrawer(null)}
      >
        <DoctorNotesTab notes={notes} onAdd={handleAddNote} />
      </SlideOverDrawer>
    </AppShell>
  );
}
